/** Connection prompts are not cancellable; retries join the outstanding wallet request. */
import type { MetaMaskProvider } from './metamask-provider';
import { WalletConnectionPendingError } from './wallet-connection-error';

export const WALLET_CONNECTION_TIMEOUT_MS = 60_000;
const pending = new WeakMap<MetaMaskProvider, Map<string, Promise<unknown>>>();

function isPendingConnectionError(error: unknown, depth = 0): boolean {
  if (!error || typeof error !== 'object' || depth > 3) return false;
  const value = error as { code?: unknown; message?: unknown; cause?: unknown; data?: { originalError?: unknown } };
  return (
    value.code === -32002 ||
    (typeof value.message === 'string' && /request of type ['"]wallet_requestPermissions['"] already pending/i.test(value.message)) ||
    isPendingConnectionError(value.cause, depth + 1) ||
    isPendingConnectionError(value.data?.originalError, depth + 1)
  );
}

export async function waitForWalletConnection<T>(request: Promise<T>, timeoutMs = WALLET_CONNECTION_TIMEOUT_MS): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () =>
            reject(
              new Error('MetaMask did not finish connecting. Open MetaMask to complete or dismiss the pending request, then try again.'),
            ),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** Lets a connection retry resume an outstanding Snap prompt without opening an account picker over it. */
export function hasPendingWalletConnection(provider: MetaMaskProvider, args: { method: string; params?: unknown }): boolean {
  return pending.get(provider)?.has(JSON.stringify(args)) ?? false;
}

/** Only used for connection, account access and chain selection, never signing or transaction submission. */
export function requestWalletConnection<T>(
  provider: MetaMaskProvider,
  args: { method: string; params?: unknown },
  timeoutMs = WALLET_CONNECTION_TIMEOUT_MS,
): Promise<T> {
  const requests = pending.get(provider) ?? new Map<string, Promise<unknown>>();
  pending.set(provider, requests);
  const key = JSON.stringify(args);
  let request = requests.get(key);
  if (!request) {
    request = Promise.resolve()
      .then(() => provider.request(args))
      .catch((error: unknown) => {
        if (isPendingConnectionError(error)) throw new WalletConnectionPendingError();
        throw error;
      });
    requests.set(key, request);
    const clear = () => requests.delete(key);
    void request.then(clear, clear);
  }
  return waitForWalletConnection(request as Promise<T>, timeoutMs);
}

/** Request interactive account access on explicit Connect, even if a locked wallet retains authorized accounts. */
export async function requestWalletAccountPermissions(provider: MetaMaskProvider): Promise<void> {
  await requestWalletConnection(provider, {
    method: 'wallet_requestPermissions',
    params: [{ eth_accounts: {} }],
  });
}
